import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { getRelativeTime } from "../../../utils/date/DateUtils";
import ConfirmationModal from "../popup/ConfirmationModal";
import {
  markNotificationAsRead,
  deleteNotification,
} from "../../../api/notifications";

function NotificationCard({
  id,
  message,
  date,
  entity,
  url,
  seen,
  model_type,
  onNotificationUpdate,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const menuRef = useRef(null);

  const handleDeleteNotification = () => {
    setModalAction("delete");
    setIsModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleMarkAsRead = () => {
    setModalAction("markAsRead");
    setIsModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleArchiveNotification = () => {
    setModalAction("archive");
    setIsModalOpen(true);
    setIsMenuOpen(false);
  };

  const confirmAction = async () => {
    if (modalAction === "delete") {
      setIsDeleting(true);
      // Small delay for animation
      setTimeout(() => {
        onNotificationUpdate(id, { deleted: true });
      }, 300);

      // Make API call (don't wait for response)
      try {
        await deleteNotification(id, model_type);
      } catch (error) {
        console.error("Failed to delete notification:", error);
        // Optionally revert the parent state on error
        // onNotificationUpdate(id, { deleted: false });
      }
    } else if (modalAction === "markAsRead") {
      setIsMarkingRead(true);
      // Small delay for animation
      setTimeout(() => {
        onNotificationUpdate(id, { seen: true });
      }, 300);

      // Make API call (don't wait for response)
      try {
        await markNotificationAsRead(id, model_type);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
        // Optionally revert the parent state on error
        // onNotificationUpdate(id, { seen: false });
      }
    } else if (modalAction === "archive") {
      console.log("Archive notification:", {
        message,
        date,
        entity,
        url,
        seen,
      });
    }
    setIsModalOpen(false);
    setModalAction(null);
  };

  const cancelAction = () => {
    setIsModalOpen(false);
    setModalAction(null);
  };

  const toggleMenu = (e) => {
    e.preventDefault(); // Prevent Link navigation
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCardClick = (e) => {
    // If clicking on dots or menu, prevent navigation
    if (
      e.target.closest(".notification-dots-container") ||
      e.target.closest(".notification-menu")
    ) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Mark notification as read when clicking the link (if not already seen)
    if (!seen && onNotificationUpdate) {
      // Optimistic update: update local state immediately
      onNotificationUpdate(id, { seen: true });

      // Fire-and-forget: make API call without blocking navigation
      markNotificationAsRead(id, model_type).catch((error) => {
        console.error("Failed to mark notification as read:", error);
        // Don't revert optimistic update since user is navigating away
      });
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div
      className={`notification-card ${seen ? "viewed" : ""} ${
        isDeleting ? "deleting" : ""
      } ${isMarkingRead ? "marking-read" : ""}`}
    >
      <Link
        className="notification-link"
        to={`/${url}`}
        onClick={handleCardClick}
      >
        <div className="notification-card-left">
          <div className="notification-content">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo2.png`}
              alt="logo"
            />
            <h4 className="notification-entity">{entity}</h4>
          </div>
          <p className="notification-message">{message}</p>
        </div>

        <div className="notification-card-right">
          <div className="notification-actions">
            <h6 className="notification-date">{getRelativeTime(date)}</h6>

            {/* Three Dots Button with Menu */}
            <div className="notification-dots-container" ref={menuRef}>
              <button className="notification-dots" onClick={toggleMenu}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/dots.svg`}
                  alt="More options"
                  className="dots-icon"
                />
              </button>

              {/* Action Menu */}
              {isMenuOpen && (
                <div className="notification-menu">
                  {!seen && (
                    <button
                      className="notification-menu-item"
                      onClick={handleMarkAsRead}
                    >
                      Mark as Read
                    </button>
                  )}
                  <button
                    className="notification-menu-item"
                    onClick={handleDeleteNotification}
                  >
                    Delete Notification
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={cancelAction}
        onConfirm={confirmAction}
        title={
          modalAction === "delete"
            ? "Delete Notification"
            : modalAction === "markAsRead"
            ? "Mark as Read"
            : modalAction === "archive"
            ? "Archive Notification"
            : "Confirm Action"
        }
        message={
          modalAction === "delete"
            ? "Are you sure you want to delete this notification? This action cannot be undone."
            : modalAction === "markAsRead"
            ? "Are you sure you want to mark this notification as read?"
            : modalAction === "archive"
            ? "Are you sure you want to archive this notification?"
            : "Are you sure you want to proceed?"
        }
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
        isLoading={isDeleting || isMarkingRead}
      />
    </div>
  );
}

export default NotificationCard;
